import React, { useState, useEffect, useCallback, useMemo } from "react";
import { API_BASE } from "../config";
import "../styles/DashboardCollectivite.css";

/* ── Formatage ───────────────────────────────────────────────── */
const TZ = "Europe/Paris";

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    weekday: "long", day: "2-digit", month: "long", year: "numeric", timeZone: TZ,
  });
};
const fmtHeure = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
};
const fmtDateISO  = (d) => {
  const s = d.toLocaleDateString("fr-FR", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: TZ });
  const [day, month, year] = s.split("/");
  return `${year}-${month}-${day}`;
};
const toLocalISO  = (d) => {
  const s = d.toLocaleDateString("fr-FR", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: TZ });
  const [day, month, year] = s.split("/");
  return `${year}-${month}-${day}`;
};

/* ── Helpers calendrier ──────────────────────────────────────── */
const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0,0,0,0); return x; };
const endOfMonth   = (d) => { const x = new Date(d); x.setMonth(x.getMonth()+1); x.setDate(0); x.setHours(23,59,59,999); return x; };
const addMonths    = (d, n) => { const x = new Date(d); x.setDate(1); x.setMonth(x.getMonth()+n); x.setHours(0,0,0,0); return x; };
const dayShort     = (d) => d.toLocaleDateString("fr-FR", { weekday: "short" });
const dayLong      = (d) => d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const monthLabel   = (d) => d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

/* ── Statuts ─────────────────────────────────────────────────── */
const STATUT_LABEL = { CONFIRME:"Confirmé", ANNULE:"Annulé", PASSE:"Passé", EN_ATTENTE:"En attente" };
const STATUT_COLOR = { CONFIRME:"#065f46", ANNULE:"#991b1b", PASSE:"#475569",  EN_ATTENTE:"#92400e" };
const STATUT_BG    = { CONFIRME:"#d1fae5", ANNULE:"#fee2e2", PASSE:"#f1f5f9",  EN_ATTENTE:"#fef3c7" };

/* ── Icônes ──────────────────────────────────────────────────── */
const Ico = {
  trash: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>,
};

/* ══════════════════════════════════════════════════════════════
   WIZARD : PRISE DE RENDEZ-VOUS
   Ordre : 1-Motif → 2-Médecin → 3-Créneau → 4-Confirmation
══════════════════════════════════════════════════════════════ */
export const PageRDVUsager = ({ user, onNavigate }) => {
  const token = user?.token;
  const [step, setStep] = useState(1);

  /* Sélections */
  const [motif,          setMotif]          = useState(null);
  const [medecin,        setMedecin]        = useState(null);
  const [creneau,        setCreneau]        = useState(null);
  const [searchQ,        setSearchQ]        = useState("");
  const [monthCursor,    setMonthCursor]    = useState(() => startOfMonth(new Date()));
  const [selectedDayISO, setSelectedDayISO] = useState(null);

  /* Données */
  const [motifs,   setMotifs]   = useState([]);
  const [medecins, setMedecins] = useState([]);
  const [creneaux, setCreneaux] = useState([]);

  /* UI */
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState(null);

  /* ── Fetch motifs (tous, sans filtre médecin) au montage ── */
  useEffect(() => {
    fetch(`${API_BASE}/usager/motifs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setMotifs(d.items || []))
      .catch(() => {});
  }, [token]);

  /* ── Fetch médecins quand step 2 ── */
  useEffect(() => {
    if (step !== 2) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (searchQ) params.set("q", searchQ);
    fetch(`${API_BASE}/usager/medecins?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setMedecins(d.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [step, token, searchQ]);

  /* ── Fetch créneaux 120 jours quand step 3 ── */
  useEffect(() => {
    if (step !== 3 || !medecin) return;
    setLoading(true);
    setCreneaux([]);
    setSelectedDayISO(null);
    const d1 = new Date(); d1.setHours(0,0,0,0);
    const d2 = new Date(); d2.setDate(d2.getDate()+120); d2.setHours(23,59,59,999);
    fetch(
      `${API_BASE}/usager/creneaux?medecin_user_id=${medecin.user_id}&date_debut=${fmtDateISO(d1)}&date_fin=${fmtDateISO(d2)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then(r => r.json())
      .then(d => {
        const items = (d.items || []).sort((a,b) => new Date(a.debut)-new Date(b.debut));
        setCreneaux(items);
        if (items.length > 0) {
          const firstISO = toLocalISO(new Date(items[0].debut));
          setSelectedDayISO(firstISO);
          setMonthCursor(startOfMonth(new Date(items[0].debut)));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [step, token, medecin]);

  /* ── Médecins filtrés par agrément du motif sélectionné ── */
  const filteredMedecins = useMemo(() => {
    if (!motif) return medecins;

    // 1. Utilise agrement_requis renvoyé par l'API
    // 2. Fallback : déduction depuis le libellé si le champ est absent
    let agr = (motif.agrement_requis || "").toUpperCase();
    if (!agr) {
      const lib = (motif.libelle || "").toLowerCase();
      if (lib.includes("permis"))     agr = "PREFECTURE";
      else if (lib.includes("ars"))   agr = "ARS";
    }

    return medecins.filter(m => {
      const mAgr = (m.agrement || "").toUpperCase();
      if (agr === "ARS")        return ["ARS", "BOTH"].includes(mAgr);
      if (agr === "PREFECTURE") return ["PREFECTURE", "BOTH"].includes(mAgr);
      return false; // agrément inconnu : on n'affiche rien plutôt que tout
    });
  }, [motif, medecins]);

  /* ── Libellé lisible de l'agrément du médecin ── */
  const agrementLabel = (agr) => {
    switch ((agr || "").toUpperCase()) {
      case "ARS":        return "Agréé ARS";
      case "PREFECTURE": return "Permis de conduire";
      case "BOTH":       return "Agréé ARS + Permis de conduire";
      case "AGREE":      return "Agréé";
      default:           return agr || "—";
    }
  };

  /* ── Valeurs calendrier ── */
  const slotsByDate = useMemo(() => {
    const map = new Map();
    for (const c of creneaux) {
      const key = toLocalISO(new Date(c.debut));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    return map;
  }, [creneaux]);

  const range120 = useMemo(() => {
    const start = new Date(); start.setHours(0,0,0,0);
    const end   = new Date(); end.setDate(end.getDate()+120); end.setHours(23,59,59,999);
    return { start, end };
  }, []);

  const isIn120Days       = useCallback((d) => d >= range120.start && d <= range120.end, [range120]);
  const monthDays         = useMemo(() => {
    const s = startOfMonth(monthCursor), e = endOfMonth(monthCursor), days = [];
    const cur = new Date(s);
    while (cur <= e) { days.push(new Date(cur)); cur.setDate(cur.getDate()+1); }
    return days;
  }, [monthCursor]);
  const visibleMonthDays  = useMemo(() => monthDays.filter(d => isIn120Days(d)), [monthDays, isIn120Days]);
  const daySlots          = useMemo(() => selectedDayISO ? (slotsByDate.get(selectedDayISO) || []) : [], [slotsByDate, selectedDayISO]);
  const selectedDay       = useMemo(() => {
    if (!selectedDayISO) return null;
    const [y,m,d] = selectedDayISO.split("-").map(Number);
    return new Date(y, m-1, d);
  }, [selectedDayISO]);
  const monthTitle        = useMemo(() => monthLabel(monthCursor), [monthCursor]);
  const canGoPrevMonth    = useMemo(() => endOfMonth(addMonths(monthCursor,-1)) >= range120.start, [monthCursor, range120]);
  const canGoNextMonth    = useMemo(() => startOfMonth(addMonths(monthCursor,1)) <= range120.end,  [monthCursor, range120]);

  /* ── Confirmation ── */
  const handleConfirm = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/usager/rdv`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          medecin_user_id: medecin.user_id,
          creneau_id:      creneau.id,
          motif_id:        motif.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setSuccess(data.item);
      else        setError(data.msg || "Erreur lors de la création du rendez-vous.");
    } catch {
      setError("Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(1); setMotif(null); setMedecin(null); setCreneau(null);
    setMonthCursor(startOfMonth(new Date())); setSelectedDayISO(null);
    setError(""); setSuccess(null); setSearchQ("");
  };


  /* ── Initiales médecin ── */
  const doctorInitials = (m) => {
    const parts = (m?.nom_complet || "").trim().split(/\s+/);
    return ((parts[0]?.[0] || "D") + (parts[1]?.[0] || "R")).toUpperCase();
  };

  /* ── Stepper horizontal ── */
  const STEPS = [
    { n: 1, label: "Motif" },
    { n: 2, label: "Médecin" },
    { n: 3, label: "Créneau" },
    { n: 4, label: "Confirmation" },
  ];
  const HorizStepper = () => (
    <div className="rdvPageStepper">
      {STEPS.map(({ n, label }, i) => (
        <React.Fragment key={n}>
          <div className={`rdvPageStep ${step === n ? "active" : step > n ? "done" : ""}`}>
            <div className="rdvPageStepBubble">{step > n ? "✓" : n}</div>
            <div className="rdvPageStepLabel">{label}</div>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`rdvPageStepLine ${step > n ? "done" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  /* ── Écran succès ── */
  if (success) return (
    <div className="rdvPageContainer">
      <div className="rdv2Header">
        <div className="rdv2Title">Rendez-vous confirmé</div>
        <button className="btnSecondary" onClick={reset}>Nouveau RDV</button>
      </div>
      <div className="rdv2Success">
        <div className="rdv2SuccessCard">
          <div className="rdv2SuccessIcon">✓</div>
          <div className="rdv2SuccessTitle">Rendez-vous confirmé !</div>
          <div className="rdv2SuccessSub">
            Référence — <span className="rdv2Ref">{success.reference}</span>
          </div>
          <div className="rdv2SuccessRecap">
            <div className="rdv2RecRow"><span>Motif</span><b>{success.motif_libelle}</b></div>
            <div className="rdv2RecRow"><span>Médecin</span><b>Dr {success.medecin_nom}</b></div>
            <div className="rdv2RecRow"><span>Date</span><b>{fmt(success.debut)}</b></div>
            <div className="rdv2RecRow"><span>Heure</span><b>{fmtHeure(success.debut)}</b></div>
          </div>
          <div className="rdv2SuccessActions">
            <button className="btnPrimary" onClick={() => { reset(); onNavigate?.("mes-rdv"); }}>Terminer</button>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Layout principal ── */
  return (
    <div className="rdvPageContainer">

      <div className="rdv2Header">
        <div className="rdv2Title">Nouveau rendez-vous</div>
        <button className="btnSecondary" onClick={reset}>Réinitialiser</button>
      </div>

      <HorizStepper />

      <div className="rdv2Body">

      {/* ── Panneau gauche : récapitulatif ── */}
      <div className="rdv2Left">
        <div className="rdv2Summary">
          <div className="rdv2SummaryTitle">Votre sélection</div>

          <div className="rdv2SumRow">
            <div className="rdv2SumLabel">Motif</div>
            <div className="rdv2SumValue">
              {motif ? motif.libelle : <span className="rdv2Muted">Non sélectionné</span>}
            </div>
            {motif && step > 1 && (
              <button className="rdv2LinkBtn" type="button" onClick={() => setStep(1)}>Modifier</button>
            )}
          </div>

          <div className="rdv2SumRow">
            <div className="rdv2SumLabel">Médecin</div>
            <div className="rdv2SumValue">
              {medecin
                ? <>{`Dr ${medecin.nom_complet}`}<div className="rdv2SumSub">{medecin.specialite}</div></>
                : <span className="rdv2Muted">Non sélectionné</span>}
            </div>
            {medecin && step > 2 && (
              <button className="rdv2LinkBtn" type="button"
                onClick={() => { setMedecin(null); setCreneau(null); setStep(2); }}>
                Modifier
              </button>
            )}
          </div>

          <div className="rdv2SumRow">
            <div className="rdv2SumLabel">Créneau</div>
            <div className="rdv2SumValue">
              {creneau
                ? <>{fmt(creneau.debut)}<div className="rdv2SumSub">{fmtHeure(creneau.debut)} → {fmtHeure(creneau.fin)}</div></>
                : <span className="rdv2Muted">Non sélectionné</span>}
            </div>
            {creneau && step > 3 && (
              <button className="rdv2LinkBtn" type="button"
                onClick={() => { setCreneau(null); setStep(3); }}>
                Modifier
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Panneau droit : contenu de l'étape ── */}
      <div className="rdv2Right">

        {/* ── ÉTAPE 1 : Motif ── */}
        {step === 1 && (
          <div className="rdv2Card">
            <div className="rdv2CardHead">
              <div>
                <div className="rdv2CardTitle">Motif de consultation</div>
                <div className="rdv2CardSub">Sélectionnez le motif de votre visite.</div>
              </div>
              <button className="btnPrimary" disabled={!motif} onClick={() => setStep(2)}>
                Continuer
              </button>
            </div>

            {motifs.length === 0 ? (
              <div className="rdv2Empty">Chargement des motifs…</div>
            ) : (
              <div style={{ display:"grid", gap:10, marginTop:8 }}>
                {motifs.map(m => {
                  const sel = motif?.id === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      className={`rdv2Row ${sel ? "active" : ""}`}
                      onClick={() => { setMotif(m); setMedecin(null); setCreneau(null); }}
                    >
                      <div style={{
                        width:28, height:28, borderRadius:"50%", flexShrink:0,
                        display:"grid", placeItems:"center",
                        background: sel ? "#158F77" : "rgba(15,23,42,0.06)",
                        border:`2px solid ${sel ? "#158F77" : "rgba(15,23,42,0.14)"}`,
                        transition:"background .15s",
                      }}>
                        {sel && (
                          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"
                            strokeLinecap="round" style={{width:13,height:13}}>
                            <path d="M20 6 9 17l-5-5"/>
                          </svg>
                        )}
                      </div>
                      <div className="rdv2RowMain">
                        <div style={{ fontWeight:800, fontSize:14, color: sel ? "#074035" : "#0a2b66" }}>
                          {m.libelle}
                        </div>
                      </div>
                      {sel && <span className="rdv2RowRight">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── ÉTAPE 2 : Médecin ── */}
        {step === 2 && (
          <div className="rdv2Card">
            <div className="rdv2CardHead">
              <div>
                <div className="rdv2CardTitle">Choisir un médecin</div>
                <div className="rdv2CardSub">Médecins compatibles avec votre motif.</div>
              </div>
              <button className="btnPrimary" disabled={!medecin} onClick={() => setStep(3)}>
                Continuer
              </button>
            </div>

            <div className="rdv2SearchRow">
              <input
                className="adminInput"
                placeholder="Rechercher par nom…"
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="rdv2Empty">Chargement…</div>
            ) : filteredMedecins.length === 0 ? (
              <div className="rdv2Empty">Aucun médecin disponible pour ce motif.</div>
            ) : (
              <div className="rdv2Doctors">
                {filteredMedecins.map(m => {
                  const sel = medecin?.user_id === m.user_id;
                  return (
                    <button
                      key={m.user_id}
                      type="button"
                      className={`rdv2DoctorRow ${sel ? "active" : ""}`}
                      onClick={() => { setMedecin(m); setCreneau(null); setStep(3); }}
                    >
                      <div className="rdv2DocAvatar">
                        <span className="rdv2DocInitials">{doctorInitials(m)}</span>
                      </div>
                      <div className="rdv2DoctorMain">
                        <div className="rdv2DoctorName">Dr {m.nom_complet}</div>
                        <div className="rdv2DoctorMeta">
                          <span>{m.specialite}</span>
                          {m.agrement && (
                            <><span className="rdv2Dot">•</span><span>{agrementLabel(m.agrement)}</span></>
                          )}
                        </div>
                        <div className="rdv2DoctorAddr">{m.adresse || "Adresse non renseignée"}</div>
                      </div>
                      <div className="rdv2Chevron">›</div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="rdv2BottomBar">
              <button className="btnSecondary" onClick={() => setStep(1)}>← Retour</button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 3 : Créneau (calendrier) ── */}
        {step === 3 && (
          <div className="rdv2Card">
            <div className="rdv2CardHead">
              <div>
                <div className="rdv2CardTitle">Choisir un créneau</div>
                <div className="rdv2CardSub">Dr {medecin?.nom_complet} — navigation par mois.</div>
              </div>
              <button className="btnPrimary" disabled={!creneau} onClick={() => setStep(4)}>
                Continuer
              </button>
            </div>

            {loading ? (
              <div className="rdv2Empty">Chargement des créneaux…</div>
            ) : creneaux.length === 0 ? (
              <div className="rdv2Empty">Aucun créneau disponible pour ce médecin.</div>
            ) : (
              <div className="rdv2CalendarWrap">
                <div className="rdv2CalHeader">
                  <button className="rdv2CalNav" type="button"
                    onClick={() => { setMonthCursor(m => addMonths(m,-1)); setCreneau(null); }}
                    disabled={!canGoPrevMonth} aria-label="Mois précédent">‹</button>
                  <div className="rdv2CalTitle">{monthTitle}</div>
                  <button className="rdv2CalNav" type="button"
                    onClick={() => { setMonthCursor(m => addMonths(m,1)); setCreneau(null); }}
                    disabled={!canGoNextMonth} aria-label="Mois suivant">›</button>
                </div>

                <div className="rdv2DayTabs">
                  {visibleMonthDays.map(d => {
                    const iso      = toLocalISO(d);
                    const hasSlots = slotsByDate.has(iso);
                    const isActive = selectedDayISO === iso;
                    return (
                      <button key={iso} type="button"
                        className={`rdv2DayTab ${isActive ? "active" : ""}`}
                        onClick={() => { setSelectedDayISO(iso); setCreneau(null); }}
                        title={dayLong(d)}>
                        <div className="rdv2DayName">{dayShort(d)}</div>
                        <div className="rdv2DayNum">{d.getDate()}</div>
                        <div className={`rdv2DayDot ${hasSlots ? "on" : "off"}`} />
                      </button>
                    );
                  })}
                </div>

                <div className="rdv2SlotsPanel">
                  <div className="rdv2SlotsHead">
                    <div className="rdv2SlotsTitle">
                      {selectedDay ? dayLong(selectedDay) : "Sélectionnez un jour"}
                    </div>
                    <div className="rdv2SlotsHint">
                      {selectedDayISO ? `${daySlots.length} créneau(x)` : ""}
                    </div>
                  </div>
                  {selectedDayISO && daySlots.length === 0 ? (
                    <div className="rdv2Empty">Aucun créneau ce jour-là.</div>
                  ) : (
                    <div className="rdv2SlotsGrid">
                      {daySlots.map(c => (
                        <button key={c.id} type="button"
                          className={`rdv2SlotBtn ${creneau?.id === c.id ? "active" : ""}`}
                          onClick={() => setCreneau(c)}>
                          <span className="rdv2SlotTime">{fmtHeure(c.debut)}</span>
                          <span className="rdv2SlotDur">{fmtHeure(c.debut)} → {fmtHeure(c.fin)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="rdv2BottomBar">
              <button className="btnSecondary" onClick={() => setStep(2)}>← Retour</button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 4 : Confirmation ── */}
        {step === 4 && (
          <div className="rdv2Card">
            <div className="rdv2CardHead">
              <div>
                <div className="rdv2CardTitle">Confirmation</div>
                <div className="rdv2CardSub">Vérifiez puis confirmez votre rendez-vous.</div>
              </div>
            </div>

            <div className="rdv2Confirm">
              <div className="rdv2ConfirmItem">
                <div className="rdv2ConfirmLabel">Motif</div>
                <div className="rdv2ConfirmValue">{motif?.libelle}</div>
              </div>
              <div className="rdv2ConfirmItem">
                <div className="rdv2ConfirmLabel">Médecin</div>
                <div className="rdv2ConfirmValue">
                  Dr {medecin?.nom_complet}
                  <div className="rdv2ConfirmSub">{medecin?.specialite}</div>
                  {medecin?.adresse && <div className="rdv2ConfirmAddress">{medecin.adresse}</div>}
                </div>
              </div>
              <div className="rdv2ConfirmItem">
                <div className="rdv2ConfirmLabel">Date & heure</div>
                <div className="rdv2ConfirmValue">
                  {fmt(creneau?.debut)}
                  <div className="rdv2ConfirmSub">{fmtHeure(creneau?.debut)} → {fmtHeure(creneau?.fin)}</div>
                </div>
              </div>
            </div>

            {error && <div className="rdvError" style={{ marginTop:12 }}>{error}</div>}

            <div className="rdv2ConfirmActions">
              <button className="btnSecondary" onClick={() => { setStep(3); setError(""); }}>
                ← Retour
              </button>
              <button className="btnPrimary" disabled={loading} onClick={handleConfirm}>
                {loading ? "Confirmation…" : "Confirmer le rendez-vous"}
              </button>
            </div>
          </div>
        )}

      </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════
   MES RENDEZ-VOUS
══════════════════════════════════════════════════════════════ */
export const PageMesRDVUsager = ({ user }) => {
  const token = user?.token;
  const [rdvs,       setRdvs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [cancelId,   setCancelId]   = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [msg,        setMsg]        = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/usager/rdv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setRdvs((d.items || []).sort((a, b) => new Date(b.debut) - new Date(a.debut))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    setCancelling(true);
    try {
      const res = await fetch(`${API_BASE}/usager/rdv/${id}/annuler`, {
        method: "PATCH",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token}` },
        body: JSON.stringify({ motif_annulation: "Annulé par l'usager" }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMsg({ type:"ok", text:"Rendez-vous annulé." });
        setCancelId(null);
        load();
      } else {
        setMsg({ type:"err", text: data.msg || "Erreur lors de l'annulation." });
      }
    } catch {
      setMsg({ type:"err", text:"Impossible de contacter le serveur." });
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="rdvLoading">Chargement de vos rendez-vous…</div>;

  return (
    <div className="rdvMesRDV">
      {msg && (
        <div className={`rdvMsgBanner ${msg.type === "ok" ? "ok" : "err"}`}>
          {msg.text}
          <button onClick={() => setMsg(null)} style={{ marginLeft:12 }}>{Ico.close}</button>
        </div>
      )}

      {rdvs.length === 0 ? (
        <div className="rdvEmpty">Vous n'avez pas encore de rendez-vous.</div>
      ) : (
        <div className="rdvList">
          {rdvs.map(rv => (
            <div key={rv.id} className="rdvCard">
              <div className="rdvCardHeader">
                <div>
                  <div className="rdvCardRef">{rv.reference}</div>
                  <div className="rdvCardDate">{fmt(rv.debut)} · {fmtHeure(rv.debut)} – {fmtHeure(rv.fin)}</div>
                </div>
                <span className="rdvCardStatut" style={{
                  background: STATUT_BG[rv.statut]    || "#f1f5f9",
                  color:      STATUT_COLOR[rv.statut] || "#64748b",
                }}>
                  {STATUT_LABEL[rv.statut] || rv.statut}
                </span>
              </div>

              <div className="rdvCardBody">
                <div className="rdvCardRow"><span className="rdvCardLbl">Médecin</span><span>Dr {rv.medecin_nom} · {rv.medecin_specialite}</span></div>
                <div className="rdvCardRow"><span className="rdvCardLbl">Adresse</span><span>{rv.medecin_adresse}</span></div>
                <div className="rdvCardRow"><span className="rdvCardLbl">Motif</span><span>{rv.motif_libelle}</span></div>
                {rv.motif_annulation && (
                  <div className="rdvCardRow">
                    <span className="rdvCardLbl">Annulation</span>
                    <span style={{ color:"#991b1b" }}>{rv.motif_annulation}</span>
                  </div>
                )}
              </div>

              {rv.statut === "CONFIRME" && (
                <div className="rdvCardFooter">
                  {cancelId === rv.id ? (
                    <div className="rdvCancelConfirm">
                      <span>Confirmer l'annulation ?</span>
                      <button className="rdvBtnDanger" disabled={cancelling}
                        onClick={() => handleCancel(rv.id)}>
                        {cancelling ? "…" : "Oui, annuler"}
                      </button>
                      <button className="rdvBtnBack" onClick={() => setCancelId(null)}>Non</button>
                    </div>
                  ) : (
                    <button className="rdvBtnAnnuler" onClick={() => setCancelId(rv.id)}>
                      {Ico.trash} Annuler ce rendez-vous
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};